import { defineStore } from "pinia";
import { computed, ref, watch } from "vue";
import { plans, questions } from "../data/content";
import { getAnalyticsContext, sendAnalyticsEvent } from "../lib/analytics";
import { createBootstrapToken, createSubmissionId, loadLatestResultIdentity, saveLatestResult } from "../lib/latest-result";
import { calculateResult } from "../lib/scoring";
import type { LatestResultIdentity, LatestResultPayload, PlanDefinition, PlanTimeBudget, ResultSyncStatus, ScoringResult, Screen } from "../types";

const SESSION_KEY = "nbti:session:v2";
const EVENTS_KEY = "nbti:events:v2";
const LEGACY_SESSION_KEY = "obti:session:v2";

interface SavedSession {
  answers: Record<string, string>;
  currentIndex: number;
  startedAt: number;
  channel: string;
  result?: ScoringResult;
  testId?: string;
  stateAnswers?: Record<string, string>;
  activePlanCode?: string;
  roleCompletedAt?: string;
  planCompletedAt?: string;
  pendingSubmissionId?: string;
  pendingBootstrapToken?: string;
  syncStatus?: ResultSyncStatus;
}

function loadSaved(): SavedSession | undefined {
  try {
    const raw = localStorage.getItem(SESSION_KEY) || localStorage.getItem(LEGACY_SESSION_KEY);
    return raw ? JSON.parse(raw) as SavedSession : undefined;
  } catch {
    return undefined;
  }
}

export const useSessionStore = defineStore("session", () => {
  const screen = ref<Screen>("home");
  const answers = ref<Record<string, string>>({});
  const currentIndex = ref(0);
  const startedAt = ref(Date.now());
  const channel = ref("default");
  const refResult = ref<string | null>(null);
  const result = ref<ScoringResult>();
  const stateAnswers = ref<Record<string, string>>({});
  const activePlan = ref<PlanDefinition>();
  const notice = ref("");
  const saved = ref(loadSaved());
  const testId = ref(saved.value?.testId || "");
  const identity = ref<LatestResultIdentity | undefined>(loadLatestResultIdentity());
  const roleCompletedAt = ref(saved.value?.roleCompletedAt || "");
  const planCompletedAt = ref(saved.value?.planCompletedAt || "");
  const pendingSubmissionId = ref(saved.value?.pendingSubmissionId || "");
  const pendingBootstrapToken = ref(saved.value?.pendingBootstrapToken || "");
  const syncStatus = ref<ResultSyncStatus>(saved.value?.syncStatus === "saved" ? "saved" : "idle");
  const syncError = ref("");
  const initialized = ref(false);

  const progress = computed(() => (currentIndex.value + 1) / questions.length);
  const currentQuestion = computed(() => questions[currentIndex.value]);

  function init() {
    const params = new URLSearchParams(window.location.search);
    channel.value = params.get("channel") || saved.value?.channel || "default";
    refResult.value = params.get("ref_result");
    if (saved.value?.result) {
      answers.value = { ...saved.value.answers };
      currentIndex.value = saved.value.currentIndex;
      startedAt.value = saved.value.startedAt;
      result.value = saved.value.result;
      testId.value = saved.value.testId || "";
      stateAnswers.value = { ...(saved.value.stateAnswers || {}) };
      activePlan.value = saved.value.activePlanCode ? plans[saved.value.activePlanCode] : undefined;
    }
    initialized.value = true;
    const context = getAnalyticsContext();
    if (context.isNewVisit) {
      track("session_start", { visit_number: context.visitNumber });
    }
    track("landing_view", { channel: channel.value, ref_result: refResult.value });
    track("page_view", { screen: screen.value, from_screen: null });
    if (pendingSubmissionId.value && isCompleteResult()) void syncLatestResult();
  }

  function persist() {
    const payload: SavedSession = {
      answers: answers.value,
      currentIndex: currentIndex.value,
      startedAt: startedAt.value,
      channel: channel.value,
      result: result.value,
      testId: testId.value,
      stateAnswers: stateAnswers.value,
      activePlanCode: activePlan.value?.code,
      roleCompletedAt: roleCompletedAt.value,
      planCompletedAt: planCompletedAt.value,
      pendingSubmissionId: pendingSubmissionId.value,
      pendingBootstrapToken: pendingBootstrapToken.value,
      syncStatus: syncStatus.value,
    };
    localStorage.setItem(SESSION_KEY, JSON.stringify(payload));
    saved.value = payload;
  }

  function start() {
    answers.value = {};
    currentIndex.value = 0;
    startedAt.value = Date.now();
    testId.value = `test_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 9)}`;
    result.value = undefined;
    stateAnswers.value = {};
    activePlan.value = undefined;
    roleCompletedAt.value = "";
    planCompletedAt.value = "";
    pendingSubmissionId.value = "";
    pendingBootstrapToken.value = "";
    syncStatus.value = "idle";
    syncError.value = "";
    screen.value = "quiz";
    persist();
    track("test_start", { channel: channel.value, test_id: testId.value });
  }

  function choose(optionId: string) {
    const question = currentQuestion.value;
    answers.value = { ...answers.value, [question.id]: optionId };
    track("question_answer", { test_id: testId.value, question_id: question.id, option_id: optionId, index: currentIndex.value + 1 });
    persist();
  }

  function next() {
    if (currentIndex.value < questions.length - 1) {
      currentIndex.value += 1;
      persist();
      return;
    }
    submit();
  }

  function previous() {
    if (currentIndex.value > 0) {
      currentIndex.value -= 1;
      persist();
    } else {
      screen.value = "home";
    }
  }

  function submit() {
    result.value = calculateResult(answers.value);
    roleCompletedAt.value = new Date().toISOString();
    screen.value = "loading";
    persist();
    track("test_submit", { test_id: testId.value, duration: Math.round((Date.now() - startedAt.value) / 1000), retry_count: 0 });
  }

  function revealResult() {
    screen.value = "result";
    persist();
    track("result_view", {
      result_code: result.value?.resultCode,
      test_id: testId.value,
      similarity: result.value?.similarity,
      secondary_code: result.value?.secondaryCode,
    });
  }

  function go(nextScreen: Screen) {
    screen.value = nextScreen;
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function isCompleteResult() {
    return Boolean(
      result.value && testId.value && roleCompletedAt.value && planCompletedAt.value && activePlan.value
      && questions.every((question) => answers.value[question.id]) && stateAnswers.value.S01 && stateAnswers.value.S02,
    );
  }

  function timeBudget(): PlanTimeBudget {
    return stateAnswers.value.S02 === "10" ? "10" : stateAnswers.value.S02 === "15" ? "15" : "5";
  }

  function chooseStateAnswer(questionId: string, value: string) {
    stateAnswers.value = { ...stateAnswers.value, [questionId]: value };
    persist();
  }

  function latestResultPayload(): LatestResultPayload {
    if (!isCompleteResult() || !result.value || !activePlan.value) throw new Error("RESULT_INCOMPLETE");
    const budget = timeBudget();
    return {
      submission_id: pendingSubmissionId.value,
      bootstrap_token: pendingBootstrapToken.value,
      test_id: testId.value,
      answers: JSON.parse(JSON.stringify(answers.value)),
      result: JSON.parse(JSON.stringify(result.value)),
      state_answers: { S01: stateAnswers.value.S01, S02: stateAnswers.value.S02 },
      plan: {
        routeCode: activePlan.value.code,
        timeBudget: budget,
        name: activePlan.value.name,
        snapshot: JSON.parse(JSON.stringify(activePlan.value.variants[budget])),
      },
      algorithm_version: "v3-weighted-1",
      content_version: "v3-2026-08-12",
      role_completed_at: roleCompletedAt.value,
      plan_completed_at: planCompletedAt.value,
    };
  }

  async function syncLatestResult() {
    if (!pendingSubmissionId.value || !isCompleteResult()) return false;
    if (syncStatus.value === "saving") return false;
    syncStatus.value = "saving";
    syncError.value = "";
    persist();
    try {
      identity.value = await saveLatestResult(latestResultPayload(), identity.value);
      pendingSubmissionId.value = "";
      pendingBootstrapToken.value = "";
      syncStatus.value = "saved";
      persist();
      track("latest_result_saved", { test_id: testId.value, user_ref: identity.value.userRef, revision: identity.value.revision });
      return true;
    } catch (error) {
      syncStatus.value = "error";
      syncError.value = error instanceof Error ? error.message : "save_failed";
      persist();
      track("latest_result_save_failed", { test_id: testId.value, error: syncError.value });
      return false;
    }
  }

  function submitState() {
    if (!stateAnswers.value.S01 || !stateAnswers.value.S02) return;
    const route = stateAnswers.value.S01;
    activePlan.value = plans[route];
    planCompletedAt.value = new Date().toISOString();
    pendingSubmissionId.value = createSubmissionId();
    pendingBootstrapToken.value = createBootstrapToken();
    syncStatus.value = "idle";
    syncError.value = "";
    screen.value = "plan";
    persist();
    void syncLatestResult();
    track("state_complete", {
      test_id: testId.value,
      state_answers: JSON.parse(JSON.stringify(stateAnswers.value)),
    });
    track("plan_view", {
      test_id: testId.value,
      route_code: route,
      time_budget: stateAnswers.value.S02,
    });
  }

  function restart() {
    track("restart_test", { previous_result_code: result.value?.resultCode });
    localStorage.removeItem(SESSION_KEY);
    saved.value = undefined;
    answers.value = {};
    currentIndex.value = 0;
    result.value = undefined;
    testId.value = "";
    stateAnswers.value = {};
    activePlan.value = undefined;
    roleCompletedAt.value = "";
    planCompletedAt.value = "";
    pendingSubmissionId.value = "";
    pendingBootstrapToken.value = "";
    syncStatus.value = "idle";
    syncError.value = "";
    screen.value = "home";
  }

  function toast(message: string) {
    notice.value = message;
    window.setTimeout(() => {
      if (notice.value === message) notice.value = "";
    }, 2200);
  }

  function track(eventName: string, properties: Record<string, unknown> = {}) {
    const context = getAnalyticsContext();
    const event = {
      event_name: eventName,
      properties: { ...properties, channel: properties.channel || channel.value },
      created_at: new Date().toISOString(),
      visitor_id: context.visitorId,
      visit_id: context.visitId,
      visit_number: context.visitNumber,
      device: context.device,
      browser: context.browser,
    };
    try {
      const queue = JSON.parse(localStorage.getItem(EVENTS_KEY) || "[]") as unknown[];
      queue.push(event);
      localStorage.setItem(EVENTS_KEY, JSON.stringify(queue.slice(-500)));
    } catch {
      // Analytics never blocks the main experience.
    }
    sendAnalyticsEvent(event);
  }

  watch(screen, (nextScreen, previousScreen) => {
    if (initialized.value) track("page_view", { screen: nextScreen, from_screen: previousScreen });
  }, { flush: "sync" });

  return {
    screen,
    answers,
    currentIndex,
    currentQuestion,
    progress,
    channel,
    refResult,
    result,
    stateAnswers,
    activePlan,
    notice,
    identity,
    syncStatus,
    syncError,
    init,
    start,
    choose,
    next,
    previous,
    revealResult,
    go,
    submitState,
    chooseStateAnswer,
    syncLatestResult,
    restart,
    toast,
    track,
  };
});
